import React, { useEffect, useRef, useState } from "react";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  CardActionArea,
  Grid,
  CardMedia,
  CardActions,
  Box,
  TextField,
} from "@mui/material";
import {
  useGetStoreProductDescriptionMutation,
  useUpdateStoreProductDetailsMutation,
} from "../../redux/reducer/features/Product/productSlice";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PageLoader from "../app/PageLoader";
import { Editor } from "@tinymce/tinymce-react";
interface ProductDetailProps {
  externalProductId: string;
  aggregateId?: string;
  setOpenShopifyDetails?: any;
  setShowSaveAlert?: any;
  setShowAlertMessage?: any;
  handleUpdateMatchingType: (productId: string, type: string) => void;
}
const ShopifyProductDetails: React.FunctionComponent<ProductDetailProps> = ({
  externalProductId: shopifyProductId,
  aggregateId,
  setOpenShopifyDetails,
  setShowSaveAlert,
  setShowAlertMessage,
  handleUpdateMatchingType,
}) => {
  const editorRef = useRef<any>(null);
  const [productDescription, setProductDescription] = React.useState<any>(null);
  const [productImages, setProductImages] = React.useState<any[]>([]);
  const [deletedImagesIds, setDeletedImagesIds] = React.useState<string[]>([]);
  const [productDetailsMutation, productDetailsMutationResult] =
    useUpdateStoreProductDetailsMutation();
  const [getProductDescriptionMutation, getProductDescriptionMutationResult] =
    useGetStoreProductDescriptionMutation();
  const [isOpen, setOpen] = React.useState(false);
  const [URL, setURL] = React.useState("");
  const handleClose = () => {
    setOpen(false);
    setOpenShopifyDetails(false);
  };

  const handleUploadClick = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      const newImage = {
        id: Math.random(),
        src: window.URL.createObjectURL(file),
        file,
        isSave: false,
      };
      setProductImages((prevImages) => [...prevImages, newImage]);
      // Reset the input value so that selecting the same file again
      // This is necessary because browsers do not fire the onChange event if the file hasn't changed.
      e.target.value = "";
    }
  };

  const handleDelete = (Id: any) => {
    setProductImages(productImages.filter((img) => img.id !== Id));
    setDeletedImagesIds((prevIds) => [...prevIds, Id]);
  };

  const handleAllSubmit = () => {
    const formData = new FormData();

    // Append title and description
    formData.append("productId", shopifyProductId);
    formData.append("aggregateId", aggregateId ?? "");
    formData.append("title", productDescription?.title ?? "");
    formData.append("description", editorRef.current.getContent() ?? "");

    // Append images
    productImages.forEach((image) => {
      if (!image.isSave && image.file) {
        formData.append("images", image.file);
      }
    });

    // Append deleted images Id's
    deletedImagesIds.forEach((id) => {
      formData.append("deletedImagesIds", id);
    });

    productDetailsMutation({ data: formData })
      .then((res: any) => {
        if (res && res.data.status === 200) {
          handleClose();
          setShowSaveAlert(true);
          setShowAlertMessage("Product details saved successfully");
          handleUpdateMatchingType(aggregateId ?? "", "Custom");
        } else {
          setShowSaveAlert(false);
          setShowAlertMessage("Failed to save product details");
        }
      })
      .catch((error) => {
        setShowSaveAlert(false);
        setShowAlertMessage(
          "Failed to save product details. Please try again."
        );
      });
  };

  useEffect(() => {
    getProductDescription();
  }, []);

  const getProductDescription = () => {
    getProductDescriptionMutation({ productId: shopifyProductId }).then(
      (res: any) => {
        if (res && res.data.status === 200) {
          setProductDescription(res.data.entity);
          setProductImages(res.data.entity?.images);
        }
      }
    );
  };

  return (
    <>
      <PageLoader
        isLoading={
          productDetailsMutationResult.isLoading ||
          getProductDescriptionMutationResult.isLoading
        }
      />
      <Box
        mt={2}
        mb={4}
        display={"flex"}
        gap={2}
        alignContent={"center"}
        justifyContent={"space-between"}
      >
        <TextField
          id="product-title"
          label="Product Title"
          fullWidth
          size="small"
          value={productDescription?.title ?? ""}
          onChange={(e) => {
            setProductDescription({
              ...productDescription,
              title: e.target.value,
            });
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={() => handleAllSubmit()}
        >
          Save
        </Button>
      </Box>
      <Box>
        <Editor
          apiKey="x605s4ekb74hvf848w5gdxgxmjspqtbadveijod91dcezrsm"
          onInit={(evt: any, editor: any) => (editorRef.current = editor)}
          init={{
            plugins: [
              "save",
              "undo",
              "redo",
              "blocks",
              "fontfamily",
              "fontsize",
              "bold",
              "italic",
              "underline",
              "strikethrough",
              "link",
              "image",
              "media",
              "table",
              "mergetags",
              "align",
              "lineheight",
              "lists",
              "checklist",
              "numlist",
              "bullist",
              "indent",
              "outdent",
              "removeformat",
            ],
            toolbar:
              "customsavebutton | undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | align lineheight | checklist numlist bullist indent outdent | removeformat",
            tinycomments_mode: "embedded",
            branding: false,
            menubar: false,
            automatic_uploads: true,
            forced_root_block: "",
            paste_remove_styles: true,
            paste_remove_spans: true,
          }}
          initialValue={productDescription?.description ?? ""}
        />
      </Box>
      <Grid mt={2} container spacing={2} width="xl">
        {productImages && productImages.length > 0
          ? productImages.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={3}
                className="list-container"
                key={item.id}
              >
                <Card sx={{ maxWidth: 345 }}>
                  <CardActionArea>
                    <CardMedia
                      component="img"
                      height="200"
                      image={item.src}
                      alt="green iguana"
                      onClick={() => {
                        setOpen(true);
                        setURL(item.src);
                      }}
                    />
                    <CardContent>
                      <Typography
                        gutterBottom
                        variant="h5"
                        component="div"
                      ></Typography>
                    </CardContent>
                  </CardActionArea>
                  <CardActions>
                    <DeleteForeverIcon
                      sx={{ color: "red", cursor: "pointer" }}
                      onClick={() => {
                        handleDelete(item.id);
                      }}
                    />
                  </CardActions>
                </Card>
              </Grid>
            ))
          : ""}

        <Grid item xs={12} sm={3}>
          <Card
            sx={{ height: "280px" }}
            onClick={() => {
              document.getElementById("image-upload-input")?.click();
            }}
          >
            <CardActionArea
              sx={{
                textAlign: "center",
                paddingTop: 14,
                paddingBottom: 13.5,
                color: "#8392ab",
              }}
            >
              <CardContent>
                <AddIcon />
                <Typography gutterBottom variant="h5" component="div">
                  New Image
                </Typography>
              </CardContent>
            </CardActionArea>
            <input
              id="image-upload-input"
              hidden
              accept="image/*"
              type="file"
              onChange={handleUploadClick}
            />
          </Card>
        </Grid>
      </Grid>
      {isOpen && <Lightbox mainSrc={URL} onCloseRequest={handleClose} />}
    </>
  );
};

export default ShopifyProductDetails;
